import { slicedToArray as _slicedToArray, objectSpread2 as _objectSpread2, extends as _extends } from '../_virtual/_rollupPluginBabelHelpers.js';
import { utils, useControllableState, useCallbackRef, Tooltip, Button, Flex, FormField, Skeleton } from '@costar/theme-ui';
import React, { forwardRef, useRef, useCallback, useEffect, useMemo } from 'react';
import { IconInfoBorder } from '@costar/theme-ui-icons/csg/IconInfoBorder';
import { IconArrowsChevronUp } from '@costar/theme-ui-icons/csg/IconArrowsChevronUp';
import { IconArrowsChevronDown } from '@costar/theme-ui-icons/csg/IconArrowsChevronDown';
import { IconArrowsChevronLeft } from '@costar/theme-ui-icons/csg/IconArrowsChevronLeft';
import { IconArrowsChevronRight } from '@costar/theme-ui-icons/csg/IconArrowsChevronRight';
import { IconArrowThickUp } from '@costar/theme-ui-icons/csg/IconArrowThickUp';
import { IconArrowThickDown } from '@costar/theme-ui-icons/csg/IconArrowThickDown';
import { IconArrowThickLeft } from '@costar/theme-ui-icons/csg/IconArrowThickLeft';
import { IconArrowThickRight } from '@costar/theme-ui-icons/csg/IconArrowThickRight';
import { FuzzySearchInput } from '@costar/theme-ui-select';
import { getResourceTransferListKey } from '../common/culture-resource-key.esm.js';
import useTransferListState from './use-transfer-list-state.esm.js';
import OptionList from './option-list.esm.js';

var DEBUG = process.env.NODE_ENV !== 'production' && false;
var emptyArray = [];
var ArrowsChevronRightIcon = /*#__PURE__*/React.createElement(IconArrowsChevronRight, {
  size: "24px"
});
var ArrowThickRightIcon = /*#__PURE__*/React.createElement(IconArrowThickRight, {
  size: "24px"
});
var ArrowThickLeftIcon = /*#__PURE__*/React.createElement(IconArrowThickLeft, {
  size: "24px"
});
var ArrowsChevronLeftIcon = /*#__PURE__*/React.createElement(IconArrowsChevronLeft, {
  size: "24px"
});
var ArrowsChevronUpIcon = /*#__PURE__*/React.createElement(IconArrowsChevronUp, {
  size: "24px"
});
var ArrowThickUpIcon = /*#__PURE__*/React.createElement(IconArrowThickUp, {
  size: "24px"
});
var ArrowThickDownIcon = /*#__PURE__*/React.createElement(IconArrowThickDown, {
  size: "24px"
});
var ArrowsChevronDownIcon = /*#__PURE__*/React.createElement(IconArrowsChevronDown, {
  size: "24px"
});
var InfoBorderIcon = /*#__PURE__*/React.createElement(IconInfoBorder, {
  color: "primary"
});
var resolveLabels = function resolveLabels(resolveText, availableOptionsLabel, selectedOptionsLabel, availableListSearchPlaceholder) {
  return {
    availableLabelText: availableOptionsLabel !== null && availableOptionsLabel !== void 0 ? availableOptionsLabel : resolveText('availableList', 'Available List'),
    selectedLabelText: selectedOptionsLabel !== null && selectedOptionsLabel !== void 0 ? selectedOptionsLabel : resolveText('selectedList', 'Selected List'),
    moveAllRightLabel: resolveText('moveAllRight', 'move all right'),
    moveSelectRightLabel: resolveText('moveSelectedRight', 'move selected right'),
    moveSelectLeftLabel: resolveText('moveSelectedLeft', 'move selected left'),
    moveAllLeftLabel: resolveText('moveAllLeft', 'move all left'),
    moveSelectedToTopLabel: resolveText('moveSelectedToTop', 'move selected to top'),
    moveSelectedUpLabel: resolveText('moveSelectedUp', 'move selected up'),
    moveSelectedDownLabel: resolveText('moveSelectedDown', 'move selected down'),
    moveSelectedToBottomLabel: resolveText('moveSelectedToBottom', 'move selected to bottom'),
    availableSearchPlaceholderText: availableListSearchPlaceholder !== null && availableListSearchPlaceholder !== void 0 ? availableListSearchPlaceholder : resolveText('availableListSearchPlaceholder', 'Search ')
  };
};
var SelectList = /*#__PURE__*/React.memo(OptionList);
var AvailableList = /*#__PURE__*/React.memo(OptionList);
var TransferList = /*#__PURE__*/forwardRef(function (props, ref) {
  var _props$selectedOption, _props$selectedOption2, _lastSelectedRef$curr;
  var availableOptionsLabel = props.availableOptionsLabel,
    _props$hiddenOptions = props.hiddenOptions,
    hiddenOptions = _props$hiddenOptions === void 0 ? emptyArray : _props$hiddenOptions,
    selectedOptionsLabel = props.selectedOptionsLabel,
    selectInfoTooltipContent = props.selectInfoTooltipContent,
    onChange = props.onChange,
    _props$variant = props.variant,
    variant = _props$variant === void 0 ? 'transferList' : _props$variant,
    _props$buttonSize = props.buttonSize,
    buttonSize = _props$buttonSize === void 0 ? 'xl' : _props$buttonSize,
    disableSort = props.disableSort,
    ignoreOnChangeUntilMounted = props.ignoreOnChangeUntilMounted,
    fontSize = props.fontSize,
    _props$labelProps = props.labelProps;
    _props$labelProps === void 0 ? utils.EMPTY_OBJECT : _props$labelProps;
    var resolveText = props.resolveText,
    isAvailableOptionsSearchable = props.isAvailableOptionsSearchable,
    isSearchClearable = props.isSearchClearable,
    availableSearchPlaceholder = props.availableSearchPlaceholder,
    availableListHeaderComponent = props.availableListHeaderComponent,
    selectListHeaderComponent = props.selectListHeaderComponent,
    selectListFooterComponent = props.selectListFooterComponent;
    props.noFuzzySearch;
    var loading = props.loading;
  var _resolveLabels = resolveLabels(getResourceTransferListKey(resolveText), availableOptionsLabel, selectedOptionsLabel, availableSearchPlaceholder),
    availableLabelText = _resolveLabels.availableLabelText,
    selectedLabelText = _resolveLabels.selectedLabelText,
    availableSearchPlaceholderText = _resolveLabels.availableSearchPlaceholderText,
    moveAllRightLabel = _resolveLabels.moveAllRightLabel,
    moveSelectRightLabel = _resolveLabels.moveSelectRightLabel,
    moveSelectLeftLabel = _resolveLabels.moveSelectLeftLabel,
    moveAllLeftLabel = _resolveLabels.moveAllLeftLabel,
    moveSelectedToTopLabel = _resolveLabels.moveSelectedToTopLabel,
    moveSelectedUpLabel = _resolveLabels.moveSelectedUpLabel,
    moveSelectedDownLabel = _resolveLabels.moveSelectedDownLabel,
    moveSelectedToBottomLabel = _resolveLabels.moveSelectedToBottomLabel;

  //initialList naming convention is because it doesn't take into account locked values, so it isn't really the selected list

  //Here we are using useControllable to allow for both controlled and uncontrolled usage
  var _useControllableState = useControllableState({
      value: (_props$selectedOption = props.selectedOptions) !== null && _props$selectedOption !== void 0 ? _props$selectedOption : [],
      defaultValue: (_props$selectedOption2 = props.selectedOptions) !== null && _props$selectedOption2 !== void 0 ? _props$selectedOption2 : [],
      //this is important for when value is passed, but not onChange
      onChange: onChange
    }),
    _useControllableState2 = _slicedToArray(_useControllableState.state, 2),
    initialList = _useControllableState2[0],
    setInitialState = _useControllableState2[1],
    isControlled = _useControllableState.isControlled;
  var _useTransferListState = useTransferListState(_objectSpread2(_objectSpread2({}, props), {}, {
      initialList: initialList,
      setInitialState: setInitialState,
      isControlled: isControlled
    })),
    availableList = _useTransferListState.availableList,
    selectedList = _useTransferListState.selectedList,
    checked = _useTransferListState.checked,
    availableChecked = _useTransferListState.availableChecked,
    selectedChecked = _useTransferListState.selectedChecked,
    firstChecked = _useTransferListState.firstChecked,
    lastChecked = _useTransferListState.lastChecked,
    lockedSelections = _useTransferListState.lockedSelections,
    handleToggle = _useTransferListState.handleToggle,
    moveAllAvailableToSelectedList = _useTransferListState.moveAllAvailableToSelectedList,
    moveCheckedAvailableToSelectedList = _useTransferListState.moveCheckedAvailableToSelectedList,
    moverCheckedSelectedToAvailableList = _useTransferListState.moverCheckedSelectedToAvailableList,
    moveAllSelectedToAvailableList = _useTransferListState.moveAllSelectedToAvailableList,
    moveAllSelectedCheckToFirst = _useTransferListState.moveAllSelectedCheckToFirst,
    moveSelectedCheckPositionForward = _useTransferListState.moveSelectedCheckPositionForward,
    moveSelectedCheckPositionBack = _useTransferListState.moveSelectedCheckPositionBack,
    moveAllSelectedCheckToLast = _useTransferListState.moveAllSelectedCheckToLast,
    activeListSearchProps = _useTransferListState.activeListSearchProps,
    defaultSelectedOptions = _useTransferListState.defaultSelectedOptions;
  var mounted = useRef(false);
  var lastSelectedRef = useRef(null);

  /* eslint-disable */
  var handleDownClick = useCallback(function () {
    setTimeout(function () {
      if (lastSelectedRef.current) {
        lastSelectedRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
          inline: 'start'
        });
      }
    }, 50);
  }, [(_lastSelectedRef$curr = lastSelectedRef.current) === null || _lastSelectedRef$curr === void 0 ? void 0 : _lastSelectedRef$curr.scrollIntoView]);
  /* eslint-enable */

  var onChangeProp = useCallbackRef(onChange);
  var revertChangeManagerRef = useRef({
    prev: defaultSelectedOptions,
    next: selectedList
  });
  useEffect(function () {
    if (ignoreOnChangeUntilMounted && mounted.current || !ignoreOnChangeUntilMounted) {
      if (DEBUG) {
        // eslint-disable-next-line no-console
        console.log('TransferList selectList changed');
      }
      // emit only if the change was not a reverted change
      // prevent double change event during reversion.
      if (!isControlled && revertChangeManagerRef.current.prev !== selectedList) {
        onChangeProp(selectedList);
      }
      revertChangeManagerRef.current = {
        prev: revertChangeManagerRef.current.next,
        next: selectedList
      };
    }
  }, [selectedList, mounted, ignoreOnChangeUntilMounted, onChangeProp, isControlled]);
  var _useMemo = useMemo(function () {
      var defaultStyles = {
        size: buttonSize
      };
      var selectionButtonProps = _objectSpread2({
        variant: variant
      }, defaultStyles);
      var sortButtonProps = _objectSpread2({
        variant: variant,
        borderRight: 'neutral.medium'
      }, defaultStyles);
      return [selectionButtonProps, sortButtonProps];
    }, [buttonSize, variant]),
    _useMemo2 = _slicedToArray(_useMemo, 2),
    selectionButtonProps = _useMemo2[0],
    sortButtonProps = _useMemo2[1];
  useEffect(function () {
    mounted.current = true;
  }, [mounted]);
  var onThickDown = useCallback(function () {
    moveSelectedCheckPositionBack();
    handleDownClick();
  }, [moveSelectedCheckPositionBack, handleDownClick]);
  var onChevronDown = useCallback(function () {
    moveAllSelectedCheckToLast();
    handleDownClick();
  }, [moveAllSelectedCheckToLast, handleDownClick]);
  var selectLabelIndicator = useMemo(function () {
    return selectInfoTooltipContent ? /*#__PURE__*/React.createElement(Tooltip, {
      arrow: true,
      p: "md",
      placement: "bottom",
      title: selectInfoTooltipContent
    }, /*#__PURE__*/React.createElement(Button, {
      icon: InfoBorderIcon,
      variant: "ghost",
      p: "0",
      size: "md"
    })) : null;
  }, [selectInfoTooltipContent]);
  // eslint-disable-next-line no-console
  // console.log(`---- rendering transfer-list ----`)
  return /*#__PURE__*/React.createElement(Flex, {
    "automation-id": "transfer-list",
    className: "csg-trl-container",
    ref: ref,
    flexDirection: "row",
    emPaddingTop: "xl",
    flexWrap: "nowrap",
    height: "100%",
    minHeight: 0
  }, /*#__PURE__*/React.createElement(Flex, {
    "automation-id": "available-fields",
    className: "csg-trl-available-column",
    minWidth: "180px",
    width: "100%",
    flexDirection: "column",
    gap: "xs"
  }, /*#__PURE__*/React.createElement(FormField, {
    label: availableLabelText,
    minHeight: 0,
    flex: "1",
    loading: loading
  }, /*#__PURE__*/React.createElement(Flex, {
    flexDirection: "column",
    gap: "sm",
    height: "100%",
    overflow: "hidden",
    minWidth: 0,
    flexGrow: 1
  }, isAvailableOptionsSearchable && (loading ? /*#__PURE__*/React.createElement(Skeleton, {
    variant: "rect",
    height: "2em"
  }) : /*#__PURE__*/React.createElement(FuzzySearchInput, _extends({
    size: fontSize
  }, activeListSearchProps, {
    placeholder: availableSearchPlaceholderText,
    isClearable: isSearchClearable
  }))), availableListHeaderComponent ? /*#__PURE__*/React.createElement(Flex, {
    flexDirection: "column",
    overflow: "hidden",
    flex: "1"
  }, !loading && availableListHeaderComponent, /*#__PURE__*/React.createElement(AvailableList, _objectSpread2({
    items: availableList,
    lastChecked: lastChecked,
    lastSelectedRef: lastSelectedRef,
    checked: checked,
    activeListSearchProps: activeListSearchProps,
    handleToggle: handleToggle
  }, props))) : /*#__PURE__*/React.createElement(React.Fragment, null, !loading && availableListHeaderComponent, /*#__PURE__*/React.createElement(AvailableList, _objectSpread2({
    items: availableList,
    lastChecked: lastChecked,
    lastSelectedRef: lastSelectedRef,
    checked: checked,
    activeListSearchProps: activeListSearchProps,
    handleToggle: handleToggle
  }, props)))))), /*#__PURE__*/React.createElement(Flex, {
    className: "csg-trl-select-buttons-column",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    minWidth: "45px",
    minHeight: 0
  }, !loading && /*#__PURE__*/React.createElement(Flex, {
    flexDirection: "column",
    "automation-id": "csg-trl-btn-group-move-left-right"
  }, /*#__PURE__*/React.createElement(Button, _extends({
    icon: ArrowsChevronRightIcon,
    onClick: moveAllAvailableToSelectedList,
    disabled: availableList.filter(function (i) {
      return !hiddenOptions.includes(i);
    }).length === 0,
    "aria-label": moveAllRightLabel,
    "automation-id": "csg-trl-btn-move-all-right"
  }, selectionButtonProps)), /*#__PURE__*/React.createElement(Button, _extends({
    icon: ArrowThickRightIcon,
    onClick: moveCheckedAvailableToSelectedList,
    disabled: availableChecked.length === 0,
    "aria-label": moveSelectRightLabel,
    "automation-id": "csg-trl-btn-move-right"
  }, selectionButtonProps)), /*#__PURE__*/React.createElement(Button, _extends({
    icon: ArrowThickLeftIcon,
    onClick: moverCheckedSelectedToAvailableList,
    disabled: selectedChecked.length === 0,
    "aria-label": moveSelectLeftLabel,
    "automation-id": "csg-trl-btn-move-left"
  }, selectionButtonProps)), /*#__PURE__*/React.createElement(Button, _extends({
    icon: ArrowsChevronLeftIcon,
    onClick: moveAllSelectedToAvailableList,
    disabled: selectedList.length <= lockedSelections.length,
    "aria-label": moveAllLeftLabel,
    "automation-id": "csg-trl-btn-move-all-left"
  }, selectionButtonProps)))), /*#__PURE__*/React.createElement(Flex, {
    "automation-id": "selected-fields",
    className: "csg-trl-selected-column",
    minWidth: "180px",
    width: "100%",
    flexDirection: "column"
  }, /*#__PURE__*/React.createElement(FormField, {
    label: selectedLabelText,
    loading: loading,
    labelIndicator: selectLabelIndicator,
    minHeight: 0,
    flexGrow: 1
  }, /*#__PURE__*/React.createElement(Flex, {
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
    minWidth: 0,
    flexGrow: 1
  }, selectListHeaderComponent, /*#__PURE__*/React.createElement(SelectList, _objectSpread2({
    items: selectedList,
    isSelectedList: true,
    lastChecked: lastChecked,
    lastSelectedRef: lastSelectedRef,
    checked: checked,
    activeListSearchProps: activeListSearchProps,
    handleToggle: handleToggle
  }, props)), selectListFooterComponent))), !disableSort && /*#__PURE__*/React.createElement(Flex, {
    className: "csg-trl-sort-buttons-column",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    minWidth: "45px",
    minHeight: 0
  }, !loading && /*#__PURE__*/React.createElement(Flex, {
    flexDirection: "column",
    "automation-id": "csg-trl-btn-group-move-up-down"
  }, /*#__PURE__*/React.createElement(Button, _extends({
    icon: ArrowsChevronUpIcon,
    onClick: moveAllSelectedCheckToFirst,
    disabled: selectedChecked.length === 0 || !!(firstChecked && selectedList.indexOf(firstChecked) === lockedSelections.length),
    "aria-label": moveSelectedToTopLabel,
    "automation-id": "csg-trl-btn-move-top"
  }, sortButtonProps)), /*#__PURE__*/React.createElement(Button, _extends({
    icon: ArrowThickUpIcon,
    onClick: moveSelectedCheckPositionForward,
    disabled: selectedChecked.length === 0 || !!(firstChecked && selectedList.indexOf(firstChecked) === lockedSelections.length),
    "aria-label": moveSelectedUpLabel,
    "automation-id": "csg-trl-btn-move-up"
  }, sortButtonProps)), /*#__PURE__*/React.createElement(Button, _extends({
    icon: ArrowThickDownIcon,
    onClick: onThickDown,
    disabled: selectedChecked.length === 0 || !!(lastChecked && selectedList.indexOf(lastChecked) === selectedList.length - 1),
    "aria-label": moveSelectedDownLabel,
    "automation-id": "csg-trl-btn-move-down"
  }, sortButtonProps)), /*#__PURE__*/React.createElement(Button, _extends({
    icon: ArrowsChevronDownIcon,
    onClick: onChevronDown,
    disabled: selectedChecked.length === 0 || !!(lastChecked && selectedList.indexOf(lastChecked) === selectedList.length - 1),
    "aria-label": moveSelectedToBottomLabel,
    "automation-id": "csg-trl-btn-move-bottom"
  }, sortButtonProps)))));
});
TransferList.displayName = 'TransferList';

export default TransferList;
//# sourceMappingURL=transfer-list.esm.js.map
